<template>
  <div class="main__content content">
    <div class="pacs-settings-table">
      <div class="pacs-settings-table__group-setting">
        <SmartInputText
          v-model="searchText"
          caption="Поиск"
          class="pacs-settings-table__settings-control pacs-settings-table__settings-control_big"
          size="l"
          @keyup.native.enter="applyTableSettings()"
        >
          <CamsButton
            position="r"
            size="l"
            type="button"
            icon-type="only"
            @click="applyTableSettings()"
          >
            <svg class="icon-search">
              <use xlink:href="#icon-search" />
            </svg>
          </CamsButton>
        </SmartInputText>
        <SmartInputDate
          v-model="archiveFrom"
          caption="Дата начала"
          class="pacs-settings-table__settings-control pacs-settings-table__settings-control_small"
          size="l"
          @input="applyTableSettings()"
        />
        <SmartInputDate
          v-model="archiveTo"
          caption="Дата конца"
          class="pacs-settings-table__settings-control pacs-settings-table__settings-control_small"
          size="l"
          @input="applyTableSettings()"
        />
        <SmartInputText
          v-model="minTemperature"
          caption="Наименьшая температура"
          class="pacs-settings-table__settings-control"
          input-type="number"
          size="l"
          step="0.1"
          @input="applyTableSettings()"
        />
        <SmartInputText
          v-model="maxTemperature"
          caption="Наибольшая температура"
          class="pacs-settings-table__settings-control"
          input-type="number"
          size="l"
          step="0.1"
          @input="applyTableSettings()"
        />
        <CamsButton
          class="pacs-settings-table__settings-control"
          priority="primary"
          size="l"
          type="button"
          @click="downloadTableAsCSV()"
        >
          Скачать отчет
        </CamsButton>
      </div>
    </div>

    <SpinnerLoading v-if="isLoading" class="loader_center" color="blue" />

    <template v-else>
      <p v-show="dataRows.length === 1000" class="pacs-settings-table__warn">
        Отображается максимально допустимое количество 1000 строк, остальные записи могли быть скрыты. Чтобы увидеть их, уточните фильтры.
      </p>
      <PacsTable
        :column-captions="columnCaptions"
        :column-names="columnNames"
        :data-rows="dataRows"
        :page-count="1"
        :total-count="dataRows.length"
        :view-only-mode="true"
      >
        <template #customThCells />
        <template #customTdCells="{dataRow}">
          <CamsButton
            icon-type="only"
            title="Скриншот события"
            type="button"
            @click="showFullScreenshotForEventInTable(dataRow['_analyticMessage_'])"
          >
            <svg>
              <use xlink:href="#icon-screen-preview" />
            </svg>
          </CamsButton>
          <CamsButton icon-type="only" title="Видео события" type="button" @click="playEventStartForEventInTable(dataRow['_analyticMessage_'])">
            <svg>
              <use xlink:href="#icon-video-preview" />
            </svg>
          </CamsButton>
          <CamsButton
            icon-type="only"
            title="Скачать видео события"
            type="button"
            @click="downloadEventVideoInTable(dataRow['_analyticMessage_'])"
          >
            <svg>
              <use xlink:href="../../../assets/img/icons.svg#cloud-archive" />
            </svg>
          </CamsButton>
        </template>
      </PacsTable>
    </template>

    <!--iframe для скачивания-->
    <iframe :src="downloadUrl" frameborder="0" height="1" width="1" />
  </div>
</template>

<script>
import {ACTION_LOAD_PACS_THERMAL_VISION_REPORT} from "@/store/pacs/analytics/index.js";
import PacsTable from "@/components/pacs/PacsTable.vue";
import {SPECIAL_TYPES_DATA_IN_CELLS} from "@/store/pacs/helpers.js";
import {eventTableMixin} from "@/components/pacs/analytics/mixins.js";
import {downloadCSV} from "@/utils/helpers.js";

/**
 * Компонент отображения таблицы и фильтров с информацией по событиям тепловизора.
 */
export default {
  name: "TableThermalVision",
  mixins: [
    eventTableMixin
  ],
  components: {
    PacsTable
  },
  data() {
    return {
      columnNames: ["text", "cameraNumber", "date", "temperature", "alarm"],
      columnCaptions: {
        "text": "Имя",
        "cameraNumber": "Номер камеры",
        "date": "Дата",
        "temperature": "Температура ℃",
        "alarm": "Температура превышена?"
      },
      // Опции фильтров.
      archiveFrom: null,
      archiveTo: null,
      minTemperature: null,
      maxTemperature: null,
      searchText: null,
    };
  },
  computed: {
    /**
     * @return {Date} Корректное представление archiveFrom в сравнении с archiveTo.
     */
    clearArchiveFrom() {
      return this.archiveFrom && this.archiveTo ? new Date(Math.min(this.archiveFrom.getTime(), this.archiveTo.getTime())) : null;
    },
    /**
     * @return {Date} Корректное представление archiveTo в сравнении с archiveFrom.
     */
    clearArchiveTo() {
      return this.archiveFrom && this.archiveTo ? new Date(Math.max(this.archiveFrom.getTime(), this.archiveTo.getTime())) : null;
    },
  },
  methods: {
    /**
     * @see eventTableMixin.methods.applyTableSettings
     */
    applyTableSettings() {
      this.stringifyQueryParams({
        f: +this.clearArchiveFrom,
        t: +this.clearArchiveTo,
        mint: this.minTemperature,
        maxt: this.maxTemperature,
        s: this.searchText
      });
    },
    /**
     * @see eventTableMixin.methods.assignQueryParams
     */
    assignQueryParams(parsedQueryParams) {
      if (_.isEmpty(parsedQueryParams)) {
        this.resetFilters();
      } else {
        this.archiveFrom = new Date(parsedQueryParams.f);
        this.archiveTo = new Date(parsedQueryParams.t);
        this.minTemperature = parsedQueryParams.mint;
        this.maxTemperature = parsedQueryParams.maxt;
        this.searchText = parsedQueryParams.s;
      }
    },
    /**
     * @see eventTableMixin.methods.loadPage.
     */
    async loadPage() {
      if (!this.clearArchiveFrom || !this.clearArchiveTo) {
        return;
      }

      this.isLoading = true;
      let responseData;
      try {
        responseData = await this.$store.dispatch(`pacs/analytics/${ACTION_LOAD_PACS_THERMAL_VISION_REPORT}`, {
          archiveFrom: this.clearArchiveFrom,
          archiveTo: this.clearArchiveTo,
          minTemperature: this.minTemperature,
          maxTemperature: this.maxTemperature,
          query: this.searchText,
        });
      } catch (error) {
        if (!error) {
          return; // Для прерванных ранее отправленных запросов.
        }
        this.$camsdals.alert("При загрузке данных произошла ошибка. Попробуйте изменить параметры фильтрации");
      }
      responseData = responseData ?? [];
      this.dataRows = responseData.map((message) => {
        return {
          "text": {
            value: message.text || "Неизвестно",
          },
          "cameraNumber": {
            value: message.cameraNumber,
          },
          "date": {
            type: SPECIAL_TYPES_DATA_IN_CELLS.DATE_TIME,
            value: message.date,
          },
          "temperature": {
            value: message.temperature,
          },
          "alarm": {
            type: SPECIAL_TYPES_DATA_IN_CELLS.BOOLEAN,
            value: message.alarm,
          },
          "_analyticMessage_": message, // Объект сообщения для передачи в события клика в кнопках на строчках в таблице. См. в этом шаблоне.
        };
      });
      this.isLoading = false;

    },
    /**
     * Сброс фильтров в начальное состояние.
     * Фильтры по умолчанию по датам за последний час.
     */
    resetFilters() {
      const archiveTo = this.maxArchiveTo || new Date(),
            archiveFrom = new Date(archiveTo.getTime());
      archiveFrom.setHours(archiveTo.getHours() - 1);
      this.archiveFrom = archiveFrom;
      this.archiveTo = archiveTo;
      this.minTemperature = 20;
      this.maxTemperature = 60;
      this.searchText = "";
    },
    /**
     * Выгрузка данных таблицы в виде CSV.
     */
    downloadTableAsCSV() {
      let csvText = "";

      const filename = `Отчет по тепловизорам.csv`,
            headers = [
              this.columnCaptions.text,
              this.columnCaptions.cameraNumber,
              this.columnCaptions.temperature,
              this.columnCaptions.date,
              this.columnCaptions.alarm,
            ];
      csvText = `${headers.join(',')}\r\n`;
      this.dataRows.forEach((message) => {
        csvText += `${message.text.value || "Неизвестный"},${message.cameraNumber.value},${message.temperature.value},${this.$options.filters.localDateTime(message.date.value)},${message.alarm.value ? "Да" : "Нет"}\r\n`;
      });
      downloadCSV(filename, csvText);
    },
  },
};
</script>
